// Web Mercator and plane-geometry helpers.
//
// These were the projection core of the hand-rolled SVG/canvas map engine. That
// engine is gone — MapLibre projects for itself — so what survives here is the
// part that is still arithmetic every surface needs: the Mercator y transform,
// the pin-label size ramp, point-in-polygon/area, centroids and ring winding.

export function mercY(lat: number): number {
  const r = (lat * Math.PI) / 180;
  return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2;
}

/** Inverse of {@link mercY}: a normalised [0,1] mercator Y back to latitude. */
export function mercYToLat(y: number): number {
  return (Math.atan(Math.sinh(Math.PI - 2 * Math.PI * y)) * 180) / Math.PI;
}

/** Pin-label size bounds in CSS px, and the zoom span over which a label grows
 *  from the floor to full size. 24 px at (near) max zoom — the historical 12 px
 *  doubled at UX's request, because the MapLibre map's labels read too small
 *  beside the pins — dropping HARD to an 8 px floor as you
 *  zoom out so a regional view's dense labels shrink away instead of tiling the
 *  screen at a fixed readable size. The ramp covers the top PIN_LABEL_ZOOM_SPAN
 *  zoom levels; below that the label stays at the floor. (Tunable: raise MIN or
 *  widen the span for a gentler effect; lower MIN for an even smaller
 *  zoomed-out label.) */
export const MAX_PIN_LABEL_PX = 24;
export const MIN_PIN_LABEL_PX = 8;
export const PIN_LABEL_ZOOM_SPAN = 5;

export function pinLabelCssPx(zoom: number, zoomMax: number): number {
  const t = (zoom - (zoomMax - PIN_LABEL_ZOOM_SPAN)) / PIN_LABEL_ZOOM_SPAN;
  const clamped = Math.max(0, Math.min(1, t));
  return MIN_PIN_LABEL_PX + (MAX_PIN_LABEL_PX - MIN_PIN_LABEL_PX) * clamped;
}

// Ray-casting point-in-polygon on raw lat/lon. Fine at hunting-ground scale
// (a few km), where treating coordinates as planar introduces no meaningful
// error. Points exactly on an edge may fall either side — acceptable here.
export function pointInPolygon(
  pt: { lat: number; lon: number },
  polygon: { lat: number; lon: number }[]
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const crosses =
      a.lat > pt.lat !== b.lat > pt.lat &&
      pt.lon < ((b.lon - a.lon) * (pt.lat - a.lat)) / (b.lat - a.lat) + a.lon;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Point-in-area for a whole ring list: inside one of the outers and outside every
 * hole. A ground with an excluded lake really does not contain a point in the lake,
 * and testing only the first ring would place a stand in the water.
 *
 * `outerCount` defaults to "every ring is an outer", which is what a single-ring or
 * hand-drawn area is.
 */
export function pointInArea(
  pt: { lat: number; lon: number },
  rings: readonly { lat: number; lon: number }[][],
  outerCount: number = rings.length
): boolean {
  const outers = rings.slice(0, outerCount);
  const holes = rings.slice(outerCount);
  return outers.some((r) => pointInPolygon(pt, r)) && !holes.some((r) => pointInPolygon(pt, r));
}

// Arithmetic mean of a set of points — a cheap "centre" good enough for
// framing a hunting ground or seeding a default pin location at the few-km
// scale these maps cover (no spherical/area weighting).
//
// An empty set has NO centre, and says so. It used to answer {lat:0,lon:0},
// which is a real coordinate in the Gulf of Guinea: a caller that forgot to
// guard got a plausible-looking answer and jumped the map there, or placed a
// label at it, instead of failing where the mistake was.
export function polygonCentroid(pts: readonly { lat: number; lon: number }[]): { lat: number; lon: number } | null {
  if (!pts.length) return null;
  return {
    lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
    lon: pts.reduce((s, p) => s + p.lon, 0) / pts.length,
  };
}

/**
 * Twice the signed area of a ring, by the shoelace formula. Only the SIGN is
 * meaningful to callers: positive when the vertices run counter-clockwise in
 * (lon, lat), negative when they run clockwise, zero for a degenerate ring.
 *
 * Nothing in this codebase normalises stored ring direction — not the web
 * client, and not the .NET side, whose WKB reader hands NTS rings back in
 * whatever orientation the provider wrote. So direction is only ever a fact to
 * be measured here, never one that can be assumed.
 *
 * Latitude is used directly rather than projected. `mercY` decreases as latitude
 * increases, so projecting flips every ring's sign uniformly; since only the
 * relative sign between an outer and its holes decides what a non-zero fill
 * cuts out, the flip cancels and the unprojected answer is the same answer.
 */
export function ringWinding(ring: readonly { lat: number; lon: number }[]): number {
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]
    const b = ring[(i + 1) % ring.length]
    sum += a.lon * b.lat - b.lon * a.lat
  }
  return sum
}
